import React, { useEffect, useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { communityAPI } from '../lib/api';
import { toast } from 'sonner';
import { Search, ArrowLeft, Trophy, Users, Crown, LayoutGrid, Swords, Star } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

export default function Community() {
  const [searchParams] = useSearchParams();
  useSEO('Community Boards | JepPrep', 'Challenge yourself with community-shared trivia boards and compete on leaderboards.', { canonical: 'https://jepprep.com/community' });
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [difficulty, setDifficulty] = useState(searchParams.get('difficulty') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadBoards();
  }, [page, difficulty, sortBy]);

  const loadBoards = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12, sort_by: sortBy };
      if (difficulty && difficulty !== 'all') params.difficulty = difficulty;
      if (search) params.search = search;

      const response = await communityAPI.browse(params);
      setBoards(response.data.boards || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      toast.error('Failed to load boards');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadBoards();
  };

  const BoardCard = ({ board }) => {
    const isChallenge = board.community_challenge;
    const categories = (board.categories || []).slice(0, 5);
    const plays = board.community_stats?.times_played || 0;

    return (
      <Link to={`/community/${board.id}`}>
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] hover:border-[hsl(var(--brand-gold-500))]/50 transition-all h-full group" data-testid="community-board-card">
          <CardContent className="p-5">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-semibold text-lg text-white truncate pr-2" data-testid="board-title">{board.title}</h3>
              <div className="flex gap-1.5 shrink-0">
                {board.featured && (
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
                    <Star className="w-3 h-3 mr-1" />
                    Featured
                  </Badge>
                )}
                {isChallenge && (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">
                    <Swords className="w-3 h-3 mr-1" />
                    Challenge
                  </Badge>
                )}
              </div>
            </div>

            {/* Categories */}
            <div className="flex flex-wrap gap-1.5 mb-3">
              {categories.map((cat, idx) => (
                <span key={cat.name || `cat-${idx}`} className="text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded bg-[hsl(var(--brand-blue-800))] text-[hsl(var(--brand-gold-500))]">
                  {cat.name}
                </span>
              ))}
              {board.categories?.length > 5 && (
                <span className="text-[10px] text-gray-500 px-1">+{board.categories.length - 5}</span>
              )}
            </div>

            {/* Difficulty + Plays */}
            <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
              <span className="capitalize">{board.difficulty || 'Medium'}</span>
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {plays} {plays === 1 ? 'play' : 'plays'}
              </span>
            </div>

            {/* Leader Score */}
            {isChallenge && (
              <div className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-[hsl(var(--brand-gold-500))]" />
                    <span className="text-gray-400 text-xs">{board.challenge_leader_name || 'Creator'}</span>
                  </div>
                  <span className="text-[hsl(var(--brand-gold-500))] font-bold text-lg" data-testid="leader-score">
                    ${(board.challenge_score || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            )}

            {/* Creator */}
            <div className="flex items-center justify-between pt-2 border-t border-[hsl(var(--border))]">
              <div className="flex items-center gap-2">
                {board.creator_avatar_url ? (
                  <img src={board.creator_avatar_url} alt="" className="w-5 h-5 rounded-full" />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-[hsl(var(--brand-blue-800))]" />
                )}
                <span className="text-xs text-gray-500">
                  {board.creator_handle ? `@${board.creator_handle}` : board.created_by_name || 'Anonymous'}
                </span>
              </div>
              {isChallenge && (
                <span className="text-emerald-400 text-xs font-medium group-hover:text-emerald-300 transition-colors">
                  Beat the Score
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-gray-300">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-12" />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <LayoutGrid className="w-7 h-7 text-[hsl(var(--brand-gold-500))]" />
            <h1 className="font-display font-bold text-3xl text-white" data-testid="community-heading">Community Boards</h1>
          </div>
          <p className="text-gray-400">Play boards shared by the community and try to beat the top score</p>
        </div>

        {/* Search & Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <form onSubmit={handleSearch} className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search boards..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-[hsl(var(--secondary))] border-[hsl(var(--border))] text-white"
                data-testid="search-input"
              />
            </div>
          </form>

          <Select value={difficulty} onValueChange={(v) => { setDifficulty(v); setPage(1); }}>
            <SelectTrigger className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] text-white" data-testid="difficulty-filter">
              <SelectValue placeholder="Difficulty" />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <SelectItem value="all" className="text-white">All Difficulties</SelectItem>
              <SelectItem value="easy" className="text-white">Easy</SelectItem>
              <SelectItem value="medium" className="text-white">Medium</SelectItem>
              <SelectItem value="hard" className="text-white">Hard</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(1); }}>
            <SelectTrigger className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] text-white" data-testid="sort-filter">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <SelectItem value="newest" className="text-white">Newest</SelectItem>
              <SelectItem value="plays" className="text-white">Most Played</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Boards Grid */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading boards...</div>
        ) : boards.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="boards-grid">
            {boards.map((board) => (
              <BoardCard key={board.id} board={board} />
            ))}
          </div>
        ) : (
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-12 text-center">
              <Trophy className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <h3 className="text-xl text-white mb-2">No community boards yet</h3>
              <p className="text-gray-400 mb-6">Be the first to share a board! Create a board, play all 3 rounds, then share your score.</p>
              <Link to="/boards/new">
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white">
                  Create a Board
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="border-[hsl(var(--border))] text-gray-300"
            >
              Previous
            </Button>
            <span className="flex items-center px-4 text-gray-400">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="border-[hsl(var(--border))] text-gray-300"
            >
              Next
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
